import './LanguageSelector.css';
import React, { JSX, useEffect, useRef, useState } from 'react';
import { Globe } from '../../assets/svg/Globe';
import { localized } from '../../config/localization';
import { AvailableLanguagesType } from '../../blechcon';

type Props = {
  readonly onClick: (language: AvailableLanguagesType) => void;
};

export function LanguageSelector({ onClick }: Props): JSX.Element {
  const selectorRef = useRef(null);
  const [languageSelector, setLanguageSelector] = useDetectOutsideClick(selectorRef, false);

  function isSameLanguage(language: string) {
    return localized.getLanguage() === language;
  }
  function activeClass(language: string) {
    return isSameLanguage(language) ? 'language-selector__item--selected' : '';
  }

  function handleClick(language: AvailableLanguagesType) {
    setLanguageSelector(false);

    if (!isSameLanguage(language)) {
      onClick(language);
    }
  }

  return (
    <div ref={selectorRef} className="language-selector">
      {languageSelector ? (
        <>
          <span className={activeClass('de')} onClick={() => handleClick('de')}>
            DE
          </span>
          <span className={activeClass('en')} onClick={() => handleClick('en')}>
            EN
          </span>
        </>
      ) : (
        <Globe onClick={() => setLanguageSelector(true)} />
      )}
    </div>
  );
}
const useDetectOutsideClick = (
  el: React.RefObject<HTMLDivElement | null>,
  initialState: boolean = false,
  options: {
    onOutsideClick?: () => void;
    closeOnEscape?: boolean;
    disabled?: boolean;
  } = {}
): [boolean, (state: boolean) => void] => {
  const { onOutsideClick, closeOnEscape = true, disabled = false } = options;
  const [isActive, setIsActive] = useState(initialState);
  const savedCallback = useRef(onOutsideClick);

  useEffect(() => {
    savedCallback.current = onOutsideClick;
  }, [onOutsideClick]);

  useEffect(() => {
    if (!isActive || disabled) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      if (el.current && !el.current.contains(target)) {
        setIsActive(false);
        savedCallback.current?.();
      }
    };

    // Events die wir überwachen
    const clickEvents: (keyof DocumentEventMap)[] = ['mousedown', 'touchstart'];

    const timeoutId = setTimeout(() => {
      // Click Events
      clickEvents.forEach((eventName) => {
        document.addEventListener(eventName, handleClickOutside as EventListener);
      });
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      clickEvents.forEach((eventName) => {
        document.removeEventListener(eventName, handleClickOutside as EventListener);
      });
    };
  }, [isActive, disabled, el]);

  return [isActive, setIsActive];
};
